// src/components/FD/PaymentReceiver.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';
import '../GL/LoanPayment.css';

const PaymentReceiver = () => {
  const [applications, setApplications] = useState([]);
  const [payments, setPayments] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchApplications();
  }, [selectedMonth]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/fd/applications-for-payment`, {
        params: { month: selectedMonth }
      });
      
      // Ensure we have valid data
      const data = Array.isArray(response.data) ? response.data : [];
      setApplications(data);
      
      // Initialize payment amounts
      const paymentState = {};
      data.forEach(app => {
        if (app && app.registration_no) {
          paymentState[app.registration_no] = '';
        }
      });
      setPayments(paymentState);
    } catch (error) {
      console.error('Error fetching applications:', error);
      console.error('Error details:', error.response?.data);
      setApplications([]); // Set empty array on error
      setPayments({});
      alert('Failed to load applications. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentChange = (registrationNo, value) => {
    setPayments(prev => ({
      ...prev,
      [registrationNo]: value
    }));
  };

  const handlePay = async (registrationNo) => {
    const paymentAmount = parseFloat(payments[registrationNo]);
    
    if (!paymentAmount || paymentAmount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    if (!window.confirm(`Confirm payment of ₹${paymentAmount.toFixed(2)} for ${registrationNo}?`)) {
      return;
    }

    try {
      setSubmitting(prev => ({ ...prev, [registrationNo]: true }));
      
      await axios.post(`${API_BASE_URL}/api/fd/receive-payment`, {
        registration_no: registrationNo,
        payment_amount: paymentAmount,
        payment_month: selectedMonth,
        payment_date: getTodayYYYYMMDD(),
        payment_mode: 'Cash',
        transaction_id: null
      });

      alert('Payment received successfully!');
      
      // Clear payment amount for this registration
      setPayments(prev => ({
        ...prev,
        [registrationNo]: ''
      }));
      
      // Refresh applications list
      fetchApplications();
    } catch (error) {
      console.error('Error receiving payment:', error);
      alert(error.response?.data?.error || 'Failed to receive payment');
    } finally {
      setSubmitting(prev => ({ ...prev, [registrationNo]: false }));
    }
  };

  const getStatusColor = (isPaid, isLucky) => {
    if (isLucky) return '#f39c12'; // Orange for lucky
    return isPaid ? '#28a745' : '#dc3545'; // Green for paid, Red for unpaid
  };

  const getStatusText = (isPaid, isLucky) => {
    if (isLucky) return 'Lucky';
    return isPaid ? 'Paid' : 'Unpaid';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading applications...</div>;
  }

  // Safety check
  if (!Array.isArray(applications)) {
    return (
      <div className="loan-payment-container">
        <div className="loan-payment-form">
          <h1>Payment Receiver</h1>
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            <p>Error: Invalid data received. Please refresh the page.</p>
            <button 
              onClick={() => fetchApplications()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '10px'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-form">
        <h1>Payment Receiver</h1>
        
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <label style={{ fontWeight: 'bold', fontSize: '16px' }}>Select Month:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '16px'
            }}
          />
        </div>
        
        {applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>No active applications found.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Registration No</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Phone No</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Pay Amount</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Remaining Amount</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Times Lucky</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => {
                  if (!app || !app.registration_no) {
                    return null; // Skip invalid entries
                  }
                  return (
                  <tr key={app.registration_no}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.registration_no || '-'}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.name || '-'}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.phone_no || '-'}</td>
                    <td style={{ 
                      padding: '12px', 
                      border: '1px solid #ddd', 
                      textAlign: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      backgroundColor: getStatusColor(app.is_paid || false, app.is_lucky || false)
                    }}>
                      {getStatusText(app.is_paid || false, app.is_lucky || false)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {app.is_lucky ? (
                        <span style={{ fontWeight: 'bold', color: '#f39c12' }}>
                          {(() => {
                            const luckyCount = parseInt(app.lucky_count) || (app.is_lucky ? 1 : 0);
                            return luckyCount > 0 ? `${luckyCount} time${luckyCount > 1 ? 's' : ''} lucky` : 'Lucky';
                          })()}
                        </span>
                      ) : (
                        (() => {
                          const luckyCount = parseInt(app.lucky_count) || 0;
                          const monthlyIntake = parseFloat(app.monthly_intake) || 50000;
                          const discountAmount = luckyCount * 5000;
                          const discountedAmount = Math.max(0, monthlyIntake - discountAmount);
                          
                          // Ensure all values are numbers
                          const safeDiscountedAmount = isNaN(discountedAmount) ? 0 : Number(discountedAmount);
                          const safeMonthlyIntake = isNaN(monthlyIntake) ? 50000 : Number(monthlyIntake);
                          const safeDiscountAmount = isNaN(discountAmount) ? 0 : Number(discountAmount);
                          
                          const placeholder = safeDiscountedAmount < safeMonthlyIntake 
                            ? `₹${safeDiscountedAmount.toFixed(2)} (after ${luckyCount}x ₹5k discount)`
                            : 'Enter amount';
                          
                          return (
                            <div>
                              <input
                                type="number"
                                value={payments[app.registration_no] || ''}
                                onChange={(e) => handlePaymentChange(app.registration_no, e.target.value)}
                                placeholder={placeholder}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px'
                                }}
                                min="0"
                                step="0.01"
                                disabled={app.is_paid || app.is_lucky}
                              />
                              {luckyCount > 0 && (
                                <div style={{ fontSize: '11px', color: '#f39c12', marginTop: '4px' }}>
                                  Pay: ₹{safeDiscountedAmount.toFixed(2)} (Original: ₹{safeMonthlyIntake.toFixed(2)} - Discount: ₹{safeDiscountAmount.toFixed(2)})
                                </div>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                      {(() => {
                        const luckyCount = parseInt(app.lucky_count) || 0;
                        const monthlyIntake = parseFloat(app.monthly_intake) || 50000;
                        const discountAmount = luckyCount * 5000;
                        const discountedAmount = Math.max(0, monthlyIntake - discountAmount);
                        const remainingAmount = app.remaining_amount !== undefined && app.remaining_amount !== null 
                          ? parseFloat(app.remaining_amount) || 0
                          : discountedAmount;
                        
                        if (app.is_lucky) {
                          const safeDiscountAmount = isNaN(discountAmount) ? 0 : Number(discountAmount);
                          return (
                            <span>
                              ₹0.00
                              {safeDiscountAmount > 0 && (
                                <span style={{ fontSize: '12px', color: '#f39c12', display: 'block', marginTop: '4px' }}>
                                  (Lucky winner - ₹{safeDiscountAmount.toFixed(2)} discount applied)
                                </span>
                              )}
                            </span>
                          );
                        }
                        
                        // Show remaining amount (already discounted)
                        const safeRemainingAmount = isNaN(remainingAmount) ? 0 : Number(remainingAmount);
                        const safeMonthlyIntake = isNaN(monthlyIntake) ? 50000 : Number(monthlyIntake);
                        const safeDiscountAmount = isNaN(discountAmount) ? 0 : Number(discountAmount);
                        
                        return (
                          <span>
                            ₹{safeRemainingAmount.toFixed(2)}
                            {luckyCount > 0 && (
                              <span style={{ fontSize: '12px', color: '#f39c12', display: 'block', marginTop: '4px' }}>
                                (Original: ₹{safeMonthlyIntake.toFixed(2)} - Discount: ₹{safeDiscountAmount.toFixed(2)})
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {(() => {
                        const luckyCount = parseInt(app.lucky_count) || (app.is_lucky ? 1 : 0);
                        if (luckyCount > 0) {
                          return (
                            <span style={{ color: '#f39c12', fontWeight: 'bold' }}>
                              {luckyCount} time{luckyCount > 1 ? 's' : ''}
                            </span>
                          );
                        }
                        return <span>-</span>;
                      })()}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {!app.is_lucky && (
                        <button
                          onClick={() => handlePay(app.registration_no)}
                          disabled={submitting[app.registration_no] || app.is_paid}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: app.is_paid ? '#6c757d' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (submitting[app.registration_no] || app.is_paid) ? 'not-allowed' : 'pointer',
                            opacity: (submitting[app.registration_no] || app.is_paid) ? 0.6 : 1
                          }}
                        >
                          {submitting[app.registration_no] ? 'Processing...' : app.is_paid ? 'Paid' : 'Pay'}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReceiver;

